/**
 * Decimal — exact fixed-point arithmetic for money and quantities.
 *
 * Floating point is never used for amounts anywhere in Woodcraft ERP
 * (0.1 + 0.2 !== 0.3). Values are stored as a BigInt "unscaled" integer plus a
 * scale (number of decimal places), exactly like PostgreSQL numeric.
 *
 *   Decimal.from('1250.50').mul('1.14').round(2)   → 1425.57
 *   Decimal.from(100).allocate([1, 1, 1], 2)        → [33.34, 33.33, 33.33]  (sums exactly)
 *
 * Dependency-free on purpose: this file runs in the browser, in Node and in tests.
 */

export type RoundingMode = 'half_up' | 'half_even' | 'down' | 'up' | 'floor' | 'ceil';

export type DecimalInput = Decimal | string | number | bigint;

const DECIMAL_RE = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

function pow10(n: number): bigint {
  return 10n ** BigInt(n);
}

export class Decimal {
  readonly unscaled: bigint;
  readonly scale: number;

  private constructor(unscaled: bigint, scale: number) {
    if (scale < 0) throw new RangeError('scale must be >= 0');
    this.unscaled = unscaled;
    this.scale = scale;
  }

  static readonly ZERO = new Decimal(0n, 0);
  static readonly ONE = new Decimal(1n, 0);
  static readonly HUNDRED = new Decimal(100n, 0);

  /** Creates a Decimal from a string ("12.50"), number, bigint or Decimal. */
  static from(value: DecimalInput): Decimal {
    if (value instanceof Decimal) return value;
    if (typeof value === 'bigint') return new Decimal(value, 0);
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) throw new TypeError(`Cannot convert ${value} to Decimal`);
      return Decimal.parse(value.toString());
    }
    return Decimal.parse(value);
  }

  static parse(text: string): Decimal {
    const s = text.trim().replace(/[,\s_]/g, '');
    const m = DECIMAL_RE.exec(s);
    if (!m || (m[2] === '' && (m[3] === undefined || m[3] === ''))) {
      throw new TypeError(`Invalid decimal: "${text}"`);
    }
    const sign = m[1] === '-' ? -1n : 1n;
    const intPart = m[2] || '0';
    const fracPart = m[3] || '';
    const exp = m[4] ? parseInt(m[4], 10) : 0;
    let unscaled = BigInt(intPart + fracPart) * sign;
    let scale = fracPart.length - exp;
    if (scale < 0) {
      unscaled *= pow10(-scale);
      scale = 0;
    }
    return new Decimal(unscaled, scale);
  }

  static isDecimalString(text: string): boolean {
    try {
      Decimal.parse(text);
      return true;
    } catch {
      return false;
    }
  }

  static sum(values: Iterable<DecimalInput>): Decimal {
    let acc = Decimal.ZERO;
    for (const v of values) acc = acc.add(v);
    return acc;
  }

  static max(a: DecimalInput, b: DecimalInput): Decimal {
    const da = Decimal.from(a), db = Decimal.from(b);
    return da.cmp(db) >= 0 ? da : db;
  }

  static min(a: DecimalInput, b: DecimalInput): Decimal {
    const da = Decimal.from(a), db = Decimal.from(b);
    return da.cmp(db) <= 0 ? da : db;
  }

  // ---------------------------------------------------------------- helpers

  private rescale(scale: number): Decimal {
    if (scale === this.scale) return this;
    if (scale > this.scale) return new Decimal(this.unscaled * pow10(scale - this.scale), scale);
    return this.round(scale);
  }

  private static align(a: Decimal, b: Decimal): [bigint, bigint, number] {
    const scale = Math.max(a.scale, b.scale);
    return [a.rescale(scale).unscaled, b.rescale(scale).unscaled, scale];
  }

  // ------------------------------------------------------------- arithmetic

  add(other: DecimalInput): Decimal {
    const [a, b, s] = Decimal.align(this, Decimal.from(other));
    return new Decimal(a + b, s);
  }

  sub(other: DecimalInput): Decimal {
    const [a, b, s] = Decimal.align(this, Decimal.from(other));
    return new Decimal(a - b, s);
  }

  mul(other: DecimalInput): Decimal {
    const o = Decimal.from(other);
    return new Decimal(this.unscaled * o.unscaled, this.scale + o.scale);
  }

  /**
   * Division at a given result scale (default: 10 places, enough for rates and
   * unit costs). Always round the result to the currency's decimals afterwards.
   */
  div(other: DecimalInput, scale = 10, mode: RoundingMode = 'half_up'): Decimal {
    const o = Decimal.from(other);
    if (o.unscaled === 0n) throw new RangeError('Division by zero');
    // result = this / o ; scale result to `scale` with one extra guard digit for rounding
    const guard = scale + 1;
    const num = this.unscaled * pow10(guard + o.scale);
    const den = o.unscaled * pow10(this.scale);
    const q = num / den;
    const r = num % den;
    // keep the remainder as a "sticky" digit so rounding sees it
    let extended = q * 10n;
    if (r !== 0n) extended += q < 0n || (q === 0n && (num < 0n) !== (den < 0n)) ? -1n : 1n;
    return new Decimal(extended, guard + 1).round(scale, mode);
  }

  neg(): Decimal {
    return new Decimal(-this.unscaled, this.scale);
  }

  abs(): Decimal {
    return this.unscaled < 0n ? this.neg() : this;
  }

  /** value × pct / 100 */
  percent(pct: DecimalInput): Decimal {
    return this.mul(pct).div(100, this.scale + Decimal.from(pct).scale + 2);
  }

  round(scale: number, mode: RoundingMode = 'half_up'): Decimal {
    if (scale >= this.scale) return this.rescale(scale);
    const factor = pow10(this.scale - scale);
    const q = this.unscaled / factor;          // truncates toward zero
    const r = this.unscaled % factor;          // same sign as dividend
    if (r === 0n) return new Decimal(q, scale);
    const negative = this.unscaled < 0n;
    const twice = (r < 0n ? -r : r) * 2n;
    let adjust = 0n;
    switch (mode) {
      case 'down':
        adjust = 0n; break;
      case 'up':
        adjust = 1n; break;
      case 'floor':
        adjust = negative ? 1n : 0n; break;
      case 'ceil':
        adjust = negative ? 0n : 1n; break;
      case 'half_even':
        if (twice > factor) adjust = 1n;
        else if (twice === factor) adjust = (q % 2n === 0n) ? 0n : 1n;
        break;
      case 'half_up':
      default:
        adjust = twice >= factor ? 1n : 0n;
    }
    const result = negative ? q - adjust : q + adjust;
    return new Decimal(result, scale);
  }

  /**
   * Splits this amount proportionally so that the parts add up EXACTLY to the
   * total (largest-remainder method). Used for tax on discounts, installment
   * schedules, cost allocation across BOQ items, etc.
   */
  allocate(ratios: DecimalInput[], scale: number, mode: RoundingMode = 'half_up'): Decimal[] {
    const rs = ratios.map((r) => Decimal.from(r));
    const totalRatio = Decimal.sum(rs);
    if (totalRatio.isZero()) throw new RangeError('Cannot allocate with zero total ratio');
    const total = this.round(scale, mode);
    const parts: Decimal[] = [];
    let allocated = Decimal.ZERO;
    for (const r of rs) {
      const p = total.mul(r).div(totalRatio, scale, 'down');
      parts.push(p);
      allocated = allocated.add(p);
    }
    // distribute the remainder cent-by-cent to the parts with the largest fractional loss
    let remainder = total.sub(allocated).rescale(scale);
    const unit = new Decimal(remainder.unscaled < 0n ? -1n : 1n, scale);
    const exact = rs.map((r) => total.mul(r).div(totalRatio, scale + 6, 'half_up'));
    const order = parts
      .map((p, i) => ({ i, loss: exact[i].sub(p).abs() }))
      .sort((a, b) => b.loss.cmp(a.loss) || a.i - b.i)
      .map((x) => x.i);
    let k = 0;
    while (!remainder.isZero()) {
      const i = order[k % order.length];
      parts[i] = parts[i].add(unit);
      remainder = remainder.sub(unit);
      k++;
    }
    return parts;
  }

  // ------------------------------------------------------------- comparison

  cmp(other: DecimalInput): -1 | 0 | 1 {
    const [a, b] = Decimal.align(this, Decimal.from(other));
    return a < b ? -1 : a > b ? 1 : 0;
  }

  eq(other: DecimalInput): boolean { return this.cmp(other) === 0; }
  lt(other: DecimalInput): boolean { return this.cmp(other) < 0; }
  lte(other: DecimalInput): boolean { return this.cmp(other) <= 0; }
  gt(other: DecimalInput): boolean { return this.cmp(other) > 0; }
  gte(other: DecimalInput): boolean { return this.cmp(other) >= 0; }
  isZero(): boolean { return this.unscaled === 0n; }
  isNegative(): boolean { return this.unscaled < 0n; }
  isPositive(): boolean { return this.unscaled > 0n; }

  // ------------------------------------------------------------- conversion

  /** Plain decimal string with exactly `scale` fraction digits ("1425.57"). */
  toString(): string {
    const negative = this.unscaled < 0n;
    let digits = (negative ? -this.unscaled : this.unscaled).toString();
    if (this.scale === 0) return (negative ? '-' : '') + digits;
    if (digits.length <= this.scale) digits = digits.padStart(this.scale + 1, '0');
    const int = digits.slice(0, digits.length - this.scale);
    const frac = digits.slice(digits.length - this.scale);
    return `${negative ? '-' : ''}${int}.${frac}`;
  }

  toFixed(scale: number, mode: RoundingMode = 'half_up'): string {
    return this.round(scale, mode).toString();
  }

  /** Lossy — only for charts and display, never for storage. */
  toNumber(): number {
    return Number(this.toString());
  }

  toJSON(): string {
    return this.toString();
  }

  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return `Decimal(${this.toString()})`;
  }
}

/** Shorthand: D('12.50') */
export const D = (v: DecimalInput): Decimal => Decimal.from(v);
